/* eslint-disable react-refresh/only-export-components */
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { List } from 'lucide-react';
import { OrderHubDetailError } from '@/app/(client)/orders-hub/-components/OrderHubDetailError';
import { OrderHubDetailHeader } from '@/app/(client)/orders-hub/-components/OrderHubDetailHeader';
import { useOrderHubOrderActions } from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderActions';
import {
  orderHubDetailQueryOptions,
  useOrderHubOrderDetail,
} from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderDetail';
import { orderHubOrderLinesQueryOptions } from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderLinesList';
import { useOrderHubReadiness } from '@/app/(client)/orders-hub/-hooks/useOrderHubReadiness';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useFeatures } from '@/hooks/useFeatures';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/orders-hub/(pages)/(detail)/$id',
)({
  beforeLoad: ({ params, location }) => {
    const bare = `/orders-hub/${params.id}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/orders-hub/$id/overview',
        params: { id: params.id },
      });
    }
  },
  loader: async ({ params }) => {
    // Warm both the order and its lines so the header readiness badges paint
    // without a flash on navigation.
    await Promise.all([
      queryClient.ensureQueryData(orderHubDetailQueryOptions(params.id)),
      queryClient.ensureQueryData(orderHubOrderLinesQueryOptions(params.id)),
    ]);
  },
  errorComponent: OrderHubDetailError,
  component: OrderHubDetailLayout,
});

function OrderHubDetailLayout() {
  const { id } = Route.useParams();
  const { isOrdersHubEnabled } = useFeatures();
  const { getTranslation: t } = useTranslator({ page: 'ordersHub' });
  const { data: order } = useOrderHubOrderDetail(id);
  const readiness = useOrderHubReadiness(id);
  const { archive, remove, isArchiving, isDeleting } =
    useOrderHubOrderActions(id);

  if (!isOrdersHubEnabled) return null;

  const orderLabel = order?.orderNumber || order?.id || id;

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <div className='px-4 sm:px-6 pt-4'>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink render={<Link to='/orders-hub' />}>
                {t('detail.breadcrumbRoot')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{orderLabel}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {order && (
        <OrderHubDetailHeader
          order={order}
          readiness={readiness}
          onArchive={archive}
          onDelete={remove}
          isArchiving={isArchiving}
          isDeleting={isDeleting}
        />
      )}

      <div className='border-b border-border px-4 sm:px-6'>
        <span className='inline-flex items-center gap-2 border-b-2 border-primary py-3 text-sm font-medium text-foreground'>
          <List className='size-4' />
          {t('detail.tabs.overview')}
        </span>
      </div>

      <div className='@container flex-1 min-w-0'>
        <Outlet />
      </div>
    </div>
  );
}
